import React, { useState } from 'react';
import { X, Download, Trash2 } from 'lucide-react';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';

interface FileAuditItem {
  id: string;
  fileName: string;
  extension: string;
  uploadedBy: string;
  uploadedOn: string;
}

interface Props {
  onClose: () => void;
  kbTitle: string;
  sourceName: string;
  files: FileAuditItem[];
}

const VectorChunkAuditModal: React.FC<Props> = ({ onClose, kbTitle, sourceName, files }) => {
  const [fileList, setFileList] = useState<FileAuditItem[]>(files);
  const [loadingIds, setLoadingIds] = useState<string[]>([]);

  const handleDownload = async (id: string, fileName: string) => {
    try {
      const response = await knowledgebaseService.downloadDocument(id);
      const blob = new Blob([response]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download the file.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    setLoadingIds(prev => [...prev, id]);

    try {
      await knowledgebaseService.deleteDocument(id);
      setFileList(prev => prev.filter(file => file.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete the file.');
    } finally {
      setLoadingIds(prev => prev.filter(x => x !== id));
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center px-2 sm:px-4 py-10 overflow-y-auto">
      <div className="w-full max-w-5xl bg-neutral-900 rounded-lg shadow-2xl border border-neutral-800 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b border-neutral-800 bg-neutral-950">
          <div>
            <h2 className="text-white font-medium text-sm">
              Uploaded Files for <span className="text-indigo-400">{kbTitle}</span>
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Source: {sourceName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-auto max-h-[65vh] min-h-[200px] text-xs">
          <table className="min-w-full text-left text-gray-300">
            <thead className="bg-neutral-950 text-gray-400 sticky top-0 z-10 text-[10px] uppercase tracking-wide border-b border-neutral-800">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">File Name</th>
                <th className="px-3 py-2">Ext</th>
                <th className="px-3 py-2">Uploader</th>
                <th className="px-3 py-2">Uploaded On</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fileList.map((f, index) => (
                <tr key={f.id} className="border-t border-neutral-800 hover:bg-neutral-800/30 transition">
                  <td className="px-3 py-2">{index + 1}</td>
                  <td className="px-3 py-2 break-all max-w-[250px]">{f.fileName}</td>
                  <td className="px-3 py-2">{f.extension}</td>
                  <td className="px-3 py-2">{f.uploadedBy}</td>
                  <td className="px-3 py-2">{new Date(f.uploadedOn).toLocaleString()}</td>
                  <td className="px-3 py-2 flex gap-2">
                    <button
                      className="text-indigo-400 hover:text-indigo-300"
                      title="Download"
                      onClick={() => handleDownload(f.id, f.fileName)}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      className="text-red-400 hover:text-red-300"
                      title="Remove"
                      disabled={loadingIds.includes(f.id)}
                      onClick={() => handleDelete(f.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {fileList.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    No files uploaded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-neutral-800 bg-neutral-950">
          <button
            onClick={onClose}
            className="text-xs px-4 py-1.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VectorChunkAuditModal;
